/**
 * ProfileCard Component (ORCH-336, ORCH-21 §12 #72; PRD User Manager UM-16)
 *
 * Purpose: A person's shared profile: First Name, Last Name, Email, Job Title,
 *          Country, Phone, Time Zone, Locale, Time Format and Language, with a live
 *          example of the formatting (ORCH-191, ORCH-413, ORCH-410). The same card serves the
 *          User Manager's user page and My Profile. A person's Company is per
 *          tenant, on their membership: the page places the tenant's CompanyField
 *          in the card (`company`) after Job Title, and saves it to the membership.
 * Logic: The profile belongs to the person. They edit it themselves and a system
 *        admin in GLOBAL can too; a tenant admin sees it read-only. The fields
 *        come from the `user` model through TemplateForm (CreateUserTemplate);
 *        Email is the account's identity and is never edited here; it sits
 *        after Last Name (ORCH-382). My Profile uses the card's own Save; the user
 *        page hides it (`hideSave`) and saves through the handle, so the page has
 *        one Save for everything (ORCH-398).
 *        Locale, Time Zone, Language and Time Format are stored NULL to follow Country / Locale;
 *        which fields follow Country is declared in metadata (`default_from_*`,
 *        useProfileDerivations). The form shows the value they resolve to
 *        (resolveDisplayed) and saves a value equal to its default as '' again
 *        (toStored), so the change check compares stored forms, field by field
 *        (diffProfile): the page's review step lists each changed field with its
 *        value before and after, and a following value or a Phone holding only
 *        its prefilled calling code is never a change (ORCH-410).
 * Dependencies: TemplateForm, buildUserProfileTemplate, LocaleFormatExample,
 *               utils/profileFormatting, useProfileDerivations, MDM cache
 *               (fd_country, fd_locale, fd_time-format, fd_time-zone, fd_language)
 */

import React, {
  forwardRef,
  type ReactNode,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { TemplateForm } from '../template-form';
import type { TemplateFormHandle } from '../template-form';
import { buildUserProfileTemplate } from '../../templates';
import { LoadingButton } from '../common';
import { userErrorMessage } from '../../services/userManagementService';
import { useAuth } from '../../context/AuthContext';
import { useMDMValues } from '../../services/mdmCacheService';
import { DEFAULT_CURRENCY_CODE } from '../../utils/formatting';
import {
  COUNTRY_FIELD,
  LANGUAGE_FIELD,
  LOCALE_FIELD,
  TIME_FORMAT_FIELD,
  TIME_ZONE_FIELD,
  resolveDisplayed,
  toStored,
  type ProfileFormattingLists,
} from '../../utils/profileFormatting';
import LocaleFormatExample from './LocaleFormatExample';
import { useProfileDerivations } from './useProfileDerivations';
import { diffProfile, type ProfileFieldChange } from './membership';

/** Active values only; one object so the filtered lists stay stable across renders. */
const ACTIVE_VALUES = {};

export interface ProfileValues {
  user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  phone?: string | null;
  country?: string | null;
  /** Locale override (an `fd_locale` value id); blank follows the Country (ORCH-191). */
  locale_override?: string | null;
  /** Time Format (an `fd_time-format` value id); blank follows the locale. */
  clock?: string | null;
  /** Time Zone override (an `fd_time-zone` value id); blank follows the Country (ORCH-410). */
  time_zone?: string | null;
  /** Language override (an `fd_language` value id); blank follows the Country (ORCH-410). */
  language?: string | null;
}

export interface ProfileCardProps {
  profile: ProfileValues;
  /** The tenant whose metadata and master data the form reads. */
  tenantId: string;
  readOnly: boolean;
  /** A line under the heading saying whose profile this is and who may change it. */
  hint: string;
  /** Saves the profile fields (fielddef ids); rejects with the server's refusal. */
  onSave: (metadata: Record<string, unknown>) => Promise<void>;
  /** The page saves through the handle: the card shows no button of its own. */
  hideSave?: boolean;
  /**
   * The fields whose stored value differs from the saved profile, in form order;
   * empty once nothing differs (ORCH-410).
   */
  onChange?: (changes: ProfileFieldChange[]) => void;
  /** The membership's Company for the tenant being viewed, placed after Job Title (ORCH-410 §4.2). */
  company?: ReactNode;
  /** Something placed in the card (the Company) has changed, so Save is offered. */
  extraDirty?: boolean;
}

export interface ProfileCardHandle {
  /** Validate and save; false when a field is invalid or the server refuses. */
  save: () => Promise<boolean>;
  /** Back to the stored profile. */
  reset: () => void;
}

const ProfileCard = forwardRef<ProfileCardHandle, ProfileCardProps>(function ProfileCard(
  {
    profile,
    tenantId,
    readOnly,
    hint,
    onSave,
    hideSave = false,
    onChange,
    company,
    extraDirty = false,
  },
  ref
) {
  const formRef = useRef<TemplateFormHandle>(null);
  const { getCurrentTenant } = useAuth();
  const currencyCode = getCurrentTenant()?.base_currency || DEFAULT_CURRENCY_CODE;
  // The lists the displayed Locale / Time Format are worked out from
  const countries = useMDMValues(COUNTRY_FIELD, tenantId, ACTIVE_VALUES);
  const locales = useMDMValues(LOCALE_FIELD, tenantId, ACTIVE_VALUES);
  const hourCycles = useMDMValues(TIME_FORMAT_FIELD, tenantId, ACTIVE_VALUES);
  const timeZones = useMDMValues(TIME_ZONE_FIELD, tenantId, ACTIVE_VALUES);
  const languages = useMDMValues(LANGUAGE_FIELD, tenantId, ACTIVE_VALUES);
  // Which fields follow Country, as the `user` view declares it (ORCH-410 §4.3)
  const { derivations, loading: derivationsLoading } = useProfileDerivations(tenantId);
  const listsLoading =
    countries.isLoading ||
    locales.isLoading ||
    hourCycles.isLoading ||
    timeZones.isLoading ||
    languages.isLoading ||
    derivationsLoading;
  const lists = useMemo<ProfileFormattingLists>(
    () => ({
      countries: countries.data,
      locales: locales.data,
      hourCycles: hourCycles.data,
      timeZones: timeZones.data,
      languages: languages.data,
    }),
    [countries.data, locales.data, hourCycles.data, timeZones.data, languages.data]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirtyState] = useState(false);
  // Reported once per distinct set of changes, so the page isn't re-rendered for nothing
  const reported = useRef('[]');
  const setChanges = (changes: ProfileFieldChange[]) => {
    setDirtyState(changes.length > 0);
    const serialized = JSON.stringify(changes);
    if (serialized === reported.current) return;
    reported.current = serialized;
    onChange?.(changes);
  };

  const initialData = useMemo(
    () => ({
      metadata: resolveDisplayed(
        {
          'fd_first-name': profile.first_name ?? '',
          'fd_last-name': profile.last_name ?? '',
          'fd_job-title': profile.job_title ?? '',
          [COUNTRY_FIELD]: profile.country ?? '',
          'fd_user-phone': profile.phone ?? '',
          [TIME_ZONE_FIELD]: profile.time_zone ?? '',
          [LOCALE_FIELD]: profile.locale_override ?? '',
          [TIME_FORMAT_FIELD]: profile.clock ?? '',
          [LANGUAGE_FIELD]: profile.language ?? '',
        },
        lists,
        derivations
      ),
    }),
    [profile, lists, derivations]
  );
  const storedInitial = useMemo(
    () => toStored(initialData.metadata, lists, derivations),
    [initialData, lists, derivations]
  );

  const template = useMemo(
    () =>
      buildUserProfileTemplate({
        email: (
          // Fills its grid cell like every renderer (shared field grid)
          <TextField
            label="Email"
            value={profile.email}
            fullWidth
            disabled
            required
            inputProps={{ 'data-testid': 'profile-email' }}
          />
        ),
        company,
        formatExample: (
          <LocaleFormatExample
            locales={lists.locales}
            hourCycles={lists.hourCycles}
            currencyCode={currencyCode}
          />
        ),
        lists,
        derivations,
      }),
    [profile.email, company, lists, derivations, currencyCode]
  );

  const handleSave = async (): Promise<boolean> => {
    const form = formRef.current;
    if (!form || !form.validate()) return false;
    setSaving(true);
    setError(null);
    try {
      await onSave(toStored({ ...(form.getData().metadata || {}) }, lists, derivations));
      setChanges([]);
      return true;
    } catch (e) {
      setError(userErrorMessage(e, 'The profile could not be saved.'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    reset: () => {
      formRef.current?.reset();
      setError(null);
      setChanges([]);
    },
  }));

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
      data-testid="profile-card"
    >
      <Box>
        <Typography variant="h6">Profile</Typography>
        <Typography variant="body2" color="text.secondary" data-testid="profile-hint">
          {hint}
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {listsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TemplateForm
          key={`profile-${profile.user_id}`}
          ref={formRef}
          template={template}
          tenantId={tenantId}
          mode="edit"
          initialData={initialData}
          onChange={(data) =>
            setChanges(
              diffProfile(storedInitial, toStored(data.metadata || {}, lists, derivations))
            )
          }
          readOnly={readOnly || saving}
        />
      )}
      {!readOnly && !hideSave && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            variant="contained"
            loading={saving}
            disabled={!dirty && !extraDirty}
            onClick={handleSave}
            data-testid="profile-save-button"
          >
            Save Profile
          </LoadingButton>
        </Box>
      )}
    </Paper>
  );
});

export default ProfileCard;
